import {
  AddSpeakerSegmentClientInput,
  AddSpeakerSegmentResult,
  CreateTranscriptionSessionClientInput,
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType,
  GetAllTranscriptionSessionsParams,
  PerformAiAnalysisResult,
  TranscriptionSession,
  UpdateTranscriptionSessionClientInput,
  RealtimeAgentResult,
  StreamingAgentUpdate,
  AgentProcessSegmentInput,
  PaginatedTranscriptionSessionsResponse,
  SegmentsDeltaResponse
} from '@/types/transcriptionSessionTypes';
import { GeneratedSummary as GeneratedSummaryResult } from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const SESSIONS_ENDPOINT = `${API_BASE_URL}/transcriptionsessions`;

// Simple in-flight de-duplication and short-lived cache for session fetches
// Prevents multiple concurrent requests and reduces bursty refetches across components
const inFlightSessionRequests = new Map<
  string,
  Promise<TranscriptionSession | null>
>();
const sessionRequestCache = new Map<
  string,
  { data: TranscriptionSession | null; expiresAt: number }
>();
const DEFAULT_SESSION_CACHE_TTL_MS = 3000; // small TTL to absorb mount-time bursts

/**
 * Helper function to acquire Auth0 access token.
 * (Copied from templateService.ts, assuming it's a shared utility or defined per service)
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      // scope: "read:sessions write:sessions" // Add specific scopes if needed
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for TranscriptionSessionService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for TranscriptionSessionService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token for TranscriptionSessionService.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Creates a new transcription session.
 * @param input - The data for the new session.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The created transcription session.
 */
export const createTranscriptionSession = async (
  input: CreateTranscriptionSessionClientInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionSession> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${SESSIONS_ENDPOINT}/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating transcription session:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create transcription session. Status: ${response.status}`
    );
  }
  return (await response.json()) as TranscriptionSession;
};

/**
 * Retrieves all transcription sessions for the user's company, with optional filters.
 * @param params - Parameters including userId, and optional filters like pagination and search.
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns A paginated response with transcription sessions and total count.
 */
export const getAllTranscriptionSessions = async (
  params: GetAllTranscriptionSessionsParams,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<PaginatedTranscriptionSessionsResponse> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const queryParams = new URLSearchParams();
  if (params.pageNumber !== undefined) {
    queryParams.append('pageNumber', params.pageNumber.toString());
  }
  if (params.pageSize !== undefined) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  if (params.sortBy !== undefined) {
    queryParams.append('sortBy', params.sortBy);
  }
  if (params.sortAscending !== undefined) {
    queryParams.append('sortAscending', params.sortAscending.toString());
  }
  if (params.searchQuery !== undefined && params.searchQuery.trim() !== '') {
    queryParams.append('searchQuery', params.searchQuery);
  }
  if (params.personId !== undefined && params.personId.trim() !== '') {
    queryParams.append('personId', params.personId);
  }
  if (
    params.createdByUserId !== undefined &&
    params.createdByUserId.trim() !== ''
  ) {
    queryParams.append('createdByUserId', params.createdByUserId);
  }

  const queryString = queryParams.toString();
  const url = queryString
    ? `${SESSIONS_ENDPOINT}?${queryString}`
    : SESSIONS_ENDPOINT;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': params.userId // userId from params is used for the header
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error fetching all transcription sessions:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch transcription sessions. Status: ${response.status}`
    );
  }
  return (await response.json()) as PaginatedTranscriptionSessionsResponse;
};

/**
 * Retrieves a specific transcription session by its ID.
 * @param sessionId - The ID (GUID string) of the session to retrieve.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The requested session or null if not found.
 */
export const getTranscriptionSessionById = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options?: { bypassCache?: boolean }
): Promise<TranscriptionSession | null> => {
  const cacheKey = `${sessionId}:${userId}`;

  // Serve from short cache if fresh (unless bypass requested)
  if (!options?.bypassCache) {
    const cached = sessionRequestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }
    // Return in-flight promise if request already underway
    const existing = inFlightSessionRequests.get(cacheKey);
    if (existing) {
      return existing;
    }
  }

  const requestPromise = (async () => {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId,
        // Hint intermediaries and browser to avoid cached responses when bypassing
        ...(options?.bypassCache
          ? { 'Cache-Control': 'no-store, no-cache', Pragma: 'no-cache' }
          : {})
      }
    });

    try {
      if (response.status === 404) {
        console.warn(`Transcription session with ID '${sessionId}' not found.`);
        // Cache null to avoid hammering nonexistent sessions briefly
        sessionRequestCache.set(cacheKey, {
          data: null,
          expiresAt: Date.now() + DEFAULT_SESSION_CACHE_TTL_MS
        });
        return null;
      }
      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        console.error(
          `Error fetching session with ID ${sessionId}:`,
          errorData
        );
        throw new Error(
          errorData.message ||
            `Failed to fetch session. Status: ${response.status}`
        );
      }
      const data = (await response.json()) as TranscriptionSession;
      // Always refresh cache with the latest data
      sessionRequestCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + DEFAULT_SESSION_CACHE_TTL_MS
      });
      return data;
    } finally {
      // Clear in-flight marker regardless of success/failure
      inFlightSessionRequests.delete(cacheKey);
    }
  })();

  // Only coalesce when not bypassing cache; for bypass we still return the promise
  if (!options?.bypassCache) {
    inFlightSessionRequests.set(cacheKey, requestPromise);
  }
  return requestPromise;
};

/**
 * Retrieves generated summaries for a transcription session.
 */
export const getSessionSummaries = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<GeneratedSummaryResult[]> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/summaries`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      errorData.message ||
        `Failed to fetch session summaries. Status: ${response.status}`
    );
  }
  return (await response.json()) as GeneratedSummaryResult[];
};

/**
 * Updates an existing transcription session.
 * @param sessionId - The ID (GUID string) of the session to update.
 * @param input - The data to update the session with. It must include the session's ID.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The updated session, or null if not found.
 */
export const updateTranscriptionSession = async (
  sessionId: string,
  input: UpdateTranscriptionSessionClientInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionSession | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  if (input.id !== sessionId) {
    console.warn(
      `Mismatch between sessionId in path (${sessionId}) and input DTO (${input.id}). Ensuring path ID is used and payload ID matches path.`
    );
  }
  // Ensure the payload 'id' matches the 'sessionId' from the route.
  const payload: UpdateTranscriptionSessionClientInput = {
    ...input,
    id: sessionId
  };

  const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(payload)
  });

  if (response.status === 404) {
    console.warn(`Session with ID '${sessionId}' not found for update.`);
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(`Error updating session with ID ${sessionId}:`, errorData);
    throw new Error(
      errorData.message ||
        `Failed to update session. Status: ${response.status}`
    );
  }
  return (await response.json()) as TranscriptionSession;
};

/**
 * Changes the template for a session, resets questions, and triggers AI analysis.
 * @param sessionId - The ID of the session.
 * @param templateId - The ID of the new template.
 * @param userId - The ID of the user performing the action.
 * @param getAccessTokenSilently - Auth0 token getter.
 * @param getAccessTokenWithPopup - Auth0 popup token getter.
 * @returns The updated session.
 */
export const changeSessionTemplate = async (
  sessionId: string,
  templateId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TranscriptionSession> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${SESSIONS_ENDPOINT}/${sessionId}/change-template`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      },
      body: JSON.stringify({ templateId })
    }
  );

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw new Error(
      errorData.message ||
        `Failed to change session template. Status: ${response.status}`
    );
  }
  return (await response.json()) as TranscriptionSession;
};

export const getSegmentsDelta = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  params?: {
    sinceSegmentId?: string | null;
    sinceAtUtc?: string | null;
  }
): Promise<SegmentsDeltaResponse | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const search = new URLSearchParams();
  if (params?.sinceSegmentId) {
    search.set('sinceSegmentId', params.sinceSegmentId);
  }
  if (params?.sinceAtUtc) {
    search.set('sinceAtUtc', params.sinceAtUtc);
  }
  const query = search.toString();

  const response = await fetch(
    `${SESSIONS_ENDPOINT}/${sessionId}/segments/delta${
      query ? `?${query}` : ''
    }`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (!response.ok) {
    console.error(
      'Failed to fetch segments delta:',
      response.status,
      response.statusText
    );
    return null;
  }

  const raw = await response.json();
  const segments = raw.segments ?? raw.Segments ?? [];
  const count =
    typeof raw.count === 'number'
      ? raw.count
      : typeof raw.Count === 'number'
      ? raw.Count
      : segments.length;
  const nextCursor = raw.nextCursor ?? raw.NextCursor ?? null;

  return {
    segments,
    count,
    nextCursor
  };
};

/**
 * Deletes a transcription session by its ID.
 * @param sessionId - The ID (GUID string) of the session to delete.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns True if deletion was successful (204 No Content), false if not found (404).
 */
export const deleteTranscriptionSession = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 204) {
    return true; // Successfully deleted
  }
  if (response.status === 404) {
    console.warn(`Session with ID '${sessionId}' not found for deletion.`);
    return false; // Not found
  }
  if (!response.ok) {
    let errorDetail = response.statusText;
    try {
      const errorData = await response.json();
      errorDetail = errorData.message || errorDetail;
    } catch (e) {
      // Ignore if parsing fails, use statusText
    }
    console.error(`Error deleting session with ID ${sessionId}:`, errorDetail);
    throw new Error(
      `Failed to delete session. Status: ${response.status}, Detail: ${errorDetail}`
    );
  }
  // Should not be reached if !response.ok throws or 204/404 are handled.
  console.warn(
    `Unexpected status code ${response.status} when deleting session with ID ${sessionId}.`
  );
  return false;
};

/**
 * Adds a speaker segment to a specific transcription session.
 * @param sessionId - The ID of the transcription session.
 * @param input - The speaker segment data.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The result of the add operation.
 */
export const addSpeakerSegment = async (
  sessionId: string,
  input: AddSpeakerSegmentClientInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<AddSpeakerSegmentResult> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/segments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error adding speaker segment to session ${sessionId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to add speaker segment. Status: ${response.status}`
    );
  }
  return (await response.json()) as AddSpeakerSegmentResult;
};

/**
 * Triggers AI analysis for a specific transcription session.
 * @param sessionId - The ID of the transcription session to analyze.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The result of the AI analysis operation.
 */
export const performAiAnalysis = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<PerformAiAnalysisResult> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/analyze`, {
    method: 'POST', // Using POST as it triggers a processing action
    headers: {
      // 'Content-Type': 'application/json', // Not strictly needed if body is empty
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
    // No body for this request as sessionId is in the URL
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error performing AI analysis for session ${sessionId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to perform AI analysis. Status: ${response.status}`
    );
  }
  return (await response.json()) as PerformAiAnalysisResult;
};

// NEW: Semantic Kernel Agent Functions

/**
 * Processes a new speaker segment using the Semantic Kernel agent for real-time analysis.
 * @param sessionId - The ID of the transcription session.
 * @param input - The speaker segment data to process.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The result of the agent's real-time processing.
 */
export const processSegmentWithAgent = async (
  sessionId: string,
  input: AgentProcessSegmentInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<RealtimeAgentResult> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${SESSIONS_ENDPOINT}/${sessionId}/agent/process-segment`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      },
      body: JSON.stringify(input)
    }
  );

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error processing segment with agent for session ${sessionId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to process segment with agent. Status: ${response.status}`
    );
  }
  return (await response.json()) as RealtimeAgentResult;
};

/**
 * Starts streaming analysis of a transcription session using the Semantic Kernel agent.
 * @param sessionId - The ID of the transcription session to analyze.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @param onUpdate - Callback function to handle streaming updates.
 * @param onError - Callback function to handle errors.
 * @param onComplete - Callback function to handle completion.
 * @returns A function to abort the streaming connection.
 */
export const streamSessionAnalysis = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  onUpdate: (update: StreamingAgentUpdate) => void,
  onError: (error: Error) => void,
  onComplete: () => void
): Promise<() => void> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const abortController = new AbortController();

  try {
    const response = await fetch(
      `${SESSIONS_ENDPOINT}/${sessionId}/agent/stream-analysis`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        signal: abortController.signal
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        errorData.message ||
          `Failed to start streaming analysis. Status: ${response.status}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Failed to get response reader for streaming');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    // Process the stream with better error handling
    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            onComplete();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = line.slice(6).trim(); // Remove 'data: ' prefix
                if (data && data !== '') {
                  const update = JSON.parse(data) as StreamingAgentUpdate;
                  if (update.type === 'completed') {
                    // Call onComplete and break the loop to refetch data in caller
                    onComplete();
                    abortController.abort();
                    break;
                  }
                  onUpdate(update);
                }
              } catch (parseError) {
                console.warn(
                  'Failed to parse streaming data:',
                  parseError,
                  'Line:',
                  line
                );
              }
            }
          }
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          onError(
            error instanceof Error ? error : new Error('Streaming error')
          );
        }
      } finally {
        reader.releaseLock();
      }
    };

    // Start processing the stream
    processStream();
  } catch (error) {
    onError(
      error instanceof Error ? error : new Error('Failed to start stream')
    );
  }

  // Return abort function
  return () => {
    abortController.abort();
  };
};

/**
 * Demonstrates the Semantic Kernel agent functionality with a mock segment.
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The demo result showing agent capabilities.
 */
export const demoSemanticKernelAgent = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<any> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${API_BASE_URL}/demo/semantic-kernel-agent`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error running Semantic Kernel agent demo:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to run agent demo. Status: ${response.status}`
    );
  }
  return await response.json();
};

// Removed legacy client-side upload; use TranscriptionDocumentService.uploadAudioDocument instead

export const finalizeTranscriptionSession = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  recordingId?: string
) => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );
    const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify({
        recordingId: recordingId
      })
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error finalizing session:', error);
    return { success: false, message: 'Finalize failed' };
  }
};

export const startRecording = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<{ id: string; startTimeUtc: string } | null> => {
  try {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      }
    });
    const res = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/recordings/start`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error('Failed to start recording', e);
    return null;
  }
};

export const stopRecording = async (
  sessionId: string,
  recordingId: string,
  userId: string,
  getAccessTokenSilently: any
): Promise<boolean> => {
  try {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      }
    });
    const res = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/recordings/${recordingId}/stop`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );
    return res.ok;
  } catch (e) {
    console.error('Failed to stop recording', e);
    return false;
  }
};

export const postSpeakerSegment = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: any,
  payload: {
    textContent: string;
    timestamp: string;
    speakerIndex?: number;
    confidenceScore?: number;
    durationSeconds?: number;
    transcriptionRecordingId: string;
    isPauseSegment?: boolean;
  }
): Promise<boolean> => {
  try {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      }
    });
    const res = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/segments`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({
          SessionId: sessionId,
          TextContent: payload.textContent,
          Timestamp: payload.timestamp,
          SpeakerIndex: payload.speakerIndex,
          ConfidenceScore: payload.confidenceScore,
          DurationSeconds: payload.durationSeconds,
          TranscriptionRecordingId: payload.transcriptionRecordingId,
          IsPauseSegment: payload.isPauseSegment ?? false
        })
      }
    );
    return res.ok;
  } catch (e) {
    console.error('Failed to add speaker segment', e);
    return false;
  }
};

/**
 * Fetches AI insights for a specific transcription session
 * @param sessionId - The ID of the transcription session
 * @param userId - The ID of the user performing the action (for X-User-Id header)
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function
 * @returns Array of AI insights for the session
 */
export const getSessionInsights = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<any[]> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(`${SESSIONS_ENDPOINT}/${sessionId}/insights`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    });

    if (!response.ok) {
      console.error('Failed to fetch session insights:', response.statusText);
      return [];
    }

    const insights = await response.json();
    return insights;
  } catch (error) {
    console.error('Error fetching session insights:', error);
    return [];
  }
};

/**
 * Deletes an AI insight from a transcription session (soft delete)
 * @param sessionId - The ID of the transcription session
 * @param insightId - The ID of the insight to delete
 * @param userId - The ID of the user performing the action (for X-User-Id header)
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function
 * @returns True if successful, false otherwise
 */
export const deleteSessionInsight = async (
  sessionId: string,
  insightId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  try {
    const accessToken = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${SESSIONS_ENDPOINT}/${sessionId}/insights/${insightId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      console.error('Failed to delete session insight:', response.statusText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting session insight:', error);
    return false;
  }
};
